/**
 * Session Artifacts Store — the documents on a session.
 *
 * Manages a session's documents (a recording, a video, a transcript, or any
 * document — as a file, a link, or pasted text). Backed by the
 * `session_artifacts` Supabase table; files live in the `session-documents`
 * bucket and are reached through `src/lib/sessionDocuments.ts`.
 *
 * Artifacts surface as provenance timeline events on the session and bubble
 * up to parent cycle/track provenance views.
 *
 * Persistence: Supabase only (no localStorage cache).
 */

import { create } from 'zustand';
import {
  fetchAll,
  insertRecord,
  updateRecord,
  deleteRecord,
  isSupabaseAvailable,
  createInitialSyncState,
  generateUUID,
  type SyncState,
} from '../../lib/supabaseSync';
import { dispatchStoreError } from '../../hooks/prioritization/useStoreErrorHandler';
import { removeSessionDocument } from '../../lib/sessionDocuments';
import type { SessionArtifact, SessionArtifactType } from '../../types/prioritization/sessionArtifacts';

export type { SessionArtifact, SessionArtifactType };

const TABLE_NAME = 'session_artifacts';

// ============================================================================
// Row serialisation
// ============================================================================

function toRow(a: SessionArtifact): Record<string, unknown> {
  const row: Record<string, unknown> = {
    id: a.id,
    session_id: a.sessionId,
    workspace_id: a.workspaceId,
    artifact_type: a.artifactType,
    title: a.title,
    created_at: a.createdAt instanceof Date ? a.createdAt.toISOString() : a.createdAt,
    updated_at: a.updatedAt instanceof Date ? a.updatedAt.toISOString() : a.updatedAt,
  };
  if (a.url != null) row.url = a.url;
  if (a.content != null) row.content = a.content;
  if (a.notes != null) row.notes = a.notes;
  if (a.createdBy != null) row.created_by = a.createdBy;
  if (a.storagePath != null) row.storage_path = a.storagePath;
  if (a.mimeType != null) row.mime_type = a.mimeType;
  if (a.sizeBytes != null) row.size_bytes = a.sizeBytes;
  return row;
}

function fromRow(row: Record<string, unknown>): SessionArtifact {
  const toDate = (v: unknown) =>
    v instanceof Date ? v : v ? new Date(v as string) : new Date();
  return {
    id: row.id as string,
    sessionId: (row.sessionId ?? row.session_id) as string,
    workspaceId: (row.workspaceId ?? row.workspace_id) as string,
    artifactType: (row.artifactType ?? row.artifact_type) as SessionArtifactType,
    title: row.title as string,
    url: ((row.url) as string | null) ?? null,
    content: ((row.content) as string | null) ?? null,
    notes: ((row.notes) as string | null) ?? null,
    storagePath: ((row.storagePath ?? row.storage_path) as string | null) ?? null,
    mimeType: ((row.mimeType ?? row.mime_type) as string | null) ?? null,
    sizeBytes: ((row.sizeBytes ?? row.size_bytes) as number | null) ?? null,
    createdBy: ((row.createdBy ?? row.created_by) as string | null) ?? null,
    createdAt: toDate(row.createdAt ?? row.created_at),
    updatedAt: toDate(row.updatedAt ?? row.updated_at),
  };
}

// ============================================================================
// Store types
// ============================================================================

interface SessionArtifactsStoreState {
  artifacts: SessionArtifact[];
  syncState: SyncState;

  fetchFromSupabase: () => Promise<void>;

  addArtifact: (params: {
    /** Supply the id when the file was uploaded first (its path carries the id). */
    id?: string;
    sessionId: string;
    workspaceId: string;
    artifactType: SessionArtifactType;
    title: string;
    url?: string;
    content?: string;
    notes?: string;
    storagePath?: string;
    mimeType?: string;
    sizeBytes?: number;
    createdBy?: string;
  }) => string;

  updateArtifact: (
    artifactId: string,
    updates: Partial<Pick<SessionArtifact, 'title' | 'url' | 'content' | 'notes'>>,
  ) => void;

  deleteArtifact: (artifactId: string) => void;

  getArtifactsForSession: (sessionId: string) => SessionArtifact[];
}

// ============================================================================
// Store
// ============================================================================

export const useSessionArtifactsStore = create<SessionArtifactsStoreState>()(
  (set, get) => ({
    artifacts: [],
    syncState: createInitialSyncState(),

    // -------------------------------------------------------------------------
    fetchFromSupabase: async () => {
      if (!isSupabaseAvailable()) return;

      set({ syncState: { ...get().syncState, isLoading: true, syncError: null } });

      const result = await fetchAll<Record<string, unknown>>(TABLE_NAME, {
        column: 'created_at',
        ascending: true,
      });

      if (result.error) {
        if (
          result.error.includes('does not exist') ||
          result.error.includes('42P01')
        ) {
          set({ syncState: { ...get().syncState, isLoading: false, syncError: null } });
          return;
        }
        set({ syncState: { ...get().syncState, isLoading: false, syncError: result.error } });
        console.error('[SessionArtifactsStore] fetch error:', result.error);
        return;
      }

      if (result.data) {
        const artifacts = result.data.map(fromRow);
        set({
          artifacts,
          syncState: {
            ...get().syncState,
            isLoading: false,
            lastSyncAt: new Date(),
            syncError: null,
          },
        });
      }
    },

    // -------------------------------------------------------------------------
    addArtifact: ({ id: givenId, sessionId, workspaceId, artifactType, title, url, content, notes, storagePath, mimeType, sizeBytes, createdBy }) => {
      const id = givenId ?? generateUUID();
      const now = new Date();
      const artifact: SessionArtifact = {
        id,
        sessionId,
        workspaceId,
        artifactType,
        title,
        url: url ?? null,
        content: content ?? null,
        notes: notes ?? null,
        storagePath: storagePath ?? null,
        mimeType: mimeType ?? null,
        sizeBytes: sizeBytes ?? null,
        createdBy: createdBy ?? null,
        createdAt: now,
        updatedAt: now,
      };

      set((state) => ({ artifacts: [...state.artifacts, artifact] }));

      if (isSupabaseAvailable()) {
        insertRecord<Record<string, unknown>>(TABLE_NAME, toRow(artifact)).then((res) => {
          if (res.error) {
            console.error('[SessionArtifactsStore] insert error:', res.error);
            window.dispatchEvent(
              new CustomEvent('store-warning', {
                detail: {
                  store: 'sessionArtifacts',
                  action: 'addArtifact',
                  message: `Document saved here only. It did not reach the database: ${res.error}`,
                },
              }),
            );
          }
        });
      }

      return id;
    },

    // -------------------------------------------------------------------------
    updateArtifact: (artifactId, updates) => {
      const now = new Date();
      set((state) => ({
        artifacts: state.artifacts.map((a) =>
          a.id === artifactId ? { ...a, ...updates, updatedAt: now } : a,
        ),
      }));

      if (isSupabaseAvailable()) {
        const artifact = get().artifacts.find((a) => a.id === artifactId);
        if (artifact) {
          updateRecord<Record<string, unknown>>(TABLE_NAME, artifactId, toRow(artifact)).then(
            (res) => {
              if (res.error) {
                console.error('[SessionArtifactsStore] update error:', res.error);
                dispatchStoreError('SessionArtifacts', 'update artifact', res.error);
              }
            },
          );
        }
      }
    },

    // -------------------------------------------------------------------------
    deleteArtifact: (artifactId) => {
      const doomed = get().artifacts.find((a) => a.id === artifactId);
      set((state) => ({
        artifacts: state.artifacts.filter((a) => a.id !== artifactId),
      }));

      if (isSupabaseAvailable()) {
        deleteRecord(TABLE_NAME, artifactId).then((res) => {
          if (res.error) {
            console.error('[SessionArtifactsStore] delete error:', res.error);
            dispatchStoreError('SessionArtifacts', 'delete document', res.error);
            return;
          }
          // The row is gone; take the file with it (best effort).
          if (doomed?.storagePath) void removeSessionDocument(doomed.storagePath);
        });
      }
    },

    // -------------------------------------------------------------------------
    getArtifactsForSession: (sessionId) =>
      get()
        .artifacts.filter((a) => a.sessionId === sessionId)
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
  }),
);
